/**
 * Rules for bulk licence assignment (APD-13993): which rows can take a licence, how groups are
 * classified, and how failures are reported.
 */
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { LicenceManagementPageLanguage } from '../../../../../../i18n'
import { ReportingAccessLevels } from '../../../../../NonReact/constants/Permissions'
import {
    IsStringEmpty,
    isStringNotEmpty,
    IsVarEmpty,
    isVarNotEmpty
} from '../../../../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../../../../React/Common/Constants/Separators'
import { isEmailAddressValid } from '../../../../../React/Common/Utils/ValidityUtils'
import { RESTAPIError, ResultType } from '../../../../../NonReact/common/API/REST/RESTAPIError'
import { ApplicationUser } from '../../../../../NonReact/common/Types/AddUser/AddUserTypes'
import { AdminUserBody } from '../../../../../NonReact/common/Types/Types'
import {
    LicenceManagementListItem,
    LicenceManagementListRow
} from '../../../../../NonReact/Types/LicenceManagementPageTypes'
import { DeviceOrAgentGroupMembershipRow } from '../../../../../NonReact/common/API/EntityAPIClient'
import {
    BulkLicence,
    BulkLicenceProfile,
    bulkAgentNumber,
    bulkLicenceLabel,
    bulkLicenceNeeds
} from './bulkLicenceProfiles'

/** CCS error raised when a reporting licence is given to a user who holds admin rights. */
export const ADMIN_USER_ERROR_CODE: string = '11299'

/** A group whose ID looks like a phone number is a call queue; anything else is a security group. */
const CALL_QUEUE_ID_PATTERN: RegExp = /^\+?\d[\d\s-]{5,}$/

/** Groups are read for this many devices at a time. */
export const GROUP_LOAD_CONCURRENCY: number = 5

/** Page size used when reading the whole licence list. */
export const BULK_LIST_PAGE_SIZE: number = 100

/** One user/device row loaded into the bulk dialog. */
export type BulkRow = {
    readonly key: string
    /** The raw list item, which says whether the row is really licensed. */
    readonly item: LicenceManagementListItem
    /** The row as the page maps it, which the shared request builders consume. */
    readonly row: LicenceManagementListRow
    /** E-mail known for the row before the operator types one. */
    readonly prefilledEmail: string
    /** Whether an application user already exists for the row. */
    readonly exists: boolean
}

export const BlockerCode = {
    LICENSED: 'licensed',
    PENDING_DELETION: 'pendingDeletion',
    NEEDS_EMAIL: 'needsEmail',
    INVALID_EMAIL: 'invalidEmail',
    NO_AGENT: 'noAgent',
    NO_DEVICE: 'noDevice'
} as const

export type BlockerCode = (typeof BlockerCode)[keyof typeof BlockerCode]

/** Friendly description of a failure, with the HTTP status and CCS code kept for support. */
export type BulkError = {
    readonly text: string
    readonly meta: string
    readonly raw: string
}

/**
 * Whether a list item already holds a reporting licence.
 *
 * @param item Raw list item.
 * @returns True when the item has a licence other than None.
 */
export const isItemLicensed = (item: LicenceManagementListItem): boolean =>
    isStringNotEmpty(item.UserLicence) && item.UserLicence !== ReportingAccessLevels.NONE

/**
 * The e-mail known for a row before the operator types one: the user's own address, otherwise the
 * telephony username when it is an e-mail address (Teams user ID, Webex primary e-mail).
 *
 * @param item Raw list item.
 * @returns The e-mail, or an empty string when none is known.
 */
export const prefilledEmailFor = (item: LicenceManagementListItem): string => {
    if (isStringNotEmpty(item.EmailAddr)) return item.EmailAddr ?? EMPTY_STRING

    if (isEmailAddressValid(item.TelSysUsername)) return item.TelSysUsername ?? EMPTY_STRING

    return EMPTY_STRING
}

/**
 * The username a row is (or will be) known by: the existing user, else the telephony username, else
 * the e-mail — the same order the page's create uses.
 *
 * @param row Bulk row.
 * @param email E-mail in effect for the row.
 * @returns The username.
 */
export const bulkUsername = (row: BulkRow, email: string): string => {
    if (isStringNotEmpty(row.row.Username)) return row.row.Username

    if (isStringNotEmpty(row.row.TelSysUsername)) return row.row.TelSysUsername

    return email
}

/**
 * Display name for a row.
 *
 * @param row Bulk row.
 * @param email E-mail in effect for the row.
 * @returns Full name, else the device description, else the e-mail.
 */
export const bulkDisplayName = (row: BulkRow, email: string): string => {
    if (isStringNotEmpty(row.row.FullName)) return row.row.FullName

    if (isStringNotEmpty(row.row.Description)) return row.row.Description

    return email
}

/**
 * Why a row cannot take a licence, or null when it can.
 *
 * @param row Bulk row.
 * @param isLicensed Whether the row holds a licence (including one assigned in this session).
 * @param email E-mail in effect for the row.
 * @param licence Licence being assigned.
 * @param profile Active licence profile.
 * @returns The blocker, or null.
 */
export const getBulkBlocker = (
    row: BulkRow,
    isLicensed: boolean,
    email: string,
    licence: BulkLicence,
    profile: BulkLicenceProfile
): Nullable<BlockerCode> => {
    if (isLicensed) return BlockerCode.LICENSED

    if (row.row.PendingDeletion) return BlockerCode.PENDING_DELETION

    if (IsStringEmpty(email)) return BlockerCode.NEEDS_EMAIL

    if (!isEmailAddressValid(email)) return BlockerCode.INVALID_EMAIL

    if (bulkLicenceNeeds(licence) === 'agent' && IsStringEmpty(bulkAgentNumber(row.row, profile))) {
        return BlockerCode.NO_AGENT
    }

    if (bulkLicenceNeeds(licence) === 'device' && IsStringEmpty(row.row.DeviceNumber)) return BlockerCode.NO_DEVICE

    return null
}

/**
 * Text shown for a blocker.
 *
 * @param language Loaded language handler.
 * @param blocker Blocker code.
 * @param profile Active licence profile, used to name the agent licence.
 * @returns The localised blocker text.
 */
export const bulkBlockerText = (
    language: CLanguageHandler,
    blocker: BlockerCode,
    profile: BulkLicenceProfile
): string => {
    switch (blocker) {
        case BlockerCode.LICENSED:
            return language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Blocker_Licensed)
        case BlockerCode.PENDING_DELETION:
            return language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Blocker_PendingDeletion)
        case BlockerCode.NEEDS_EMAIL:
            return language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Blocker_NeedsEmail)
        case BlockerCode.INVALID_EMAIL:
            return language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Blocker_InvalidEmail)
        case BlockerCode.NO_AGENT:
            return language.getInterpolated(
                LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Blocker_NoAgent,
                bulkLicenceLabel(language, profile, ReportingAccessLevels.CALL_CENTRE_AGENT)
            )
        default:
            return language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Blocker_NoDevice)
    }
}

/**
 * Whether a group is a call queue (its ID looks like a phone number) rather than a security group.
 *
 * @param groupID Group ID.
 * @returns True for a call queue.
 */
export const isCallQueueGroup = (groupID: string): boolean => CALL_QUEUE_ID_PATTERN.test(groupID)

/** A group offered in the group filter, with its member counts. */
export type BulkGroupSummary = {
    readonly id: string
    readonly label: string
    readonly isQueue: boolean
    /** Rows in the group. */
    readonly total: number
    /** Unlicensed rows in the group. */
    readonly unlicensed: number
}

/**
 * Collects the groups the loaded rows belong to, with their member counts.
 *
 * @param rows Loaded rows.
 * @param groupsByKey Group memberships per row key.
 * @returns Group summaries keyed by group ID.
 */
export const buildGroupSummaries = (
    rows: readonly BulkRow[],
    groupsByKey: ReadonlyMap<string, readonly DeviceOrAgentGroupMembershipRow[]>
): ReadonlyMap<string, BulkGroupSummary> => {
    const summaries: Map<string, BulkGroupSummary> = new Map<string, BulkGroupSummary>()

    rows.forEach((row: BulkRow): void => {
        const unlicensed: boolean = !isItemLicensed(row.item)

        ;(groupsByKey.get(row.key) ?? []).forEach((group: DeviceOrAgentGroupMembershipRow): void => {
            const current: Definable<BulkGroupSummary> = summaries.get(group.ID)

            summaries.set(group.ID, {
                id: group.ID,
                label: current?.label ?? (isStringNotEmpty(group.Description) ? group.Description : group.ID),
                isQueue: isCallQueueGroup(group.ID),
                total: (current?.total ?? 0) + 1,
                unlicensed: (current?.unlicensed ?? 0) + (unlicensed ? 1 : 0)
            })
        })
    })

    return summaries
}

/**
 * Whether a row matches the search text (name, e-mail, device and agent numbers, partition).
 *
 * @param row Bulk row.
 * @param name Display name in effect for the row.
 * @param email E-mail in effect for the row.
 * @param query Lower-cased search text.
 * @returns True when the row matches, or the search is empty.
 */
export const matchesBulkSearch = (row: BulkRow, name: string, email: string, query: string): boolean => {
    if (IsStringEmpty(query)) return true

    return [name, email, row.row.DeviceNumber, row.row.AgentNumber, row.row.PartitionDescription]
        .join(' ')
        .toLowerCase()
        .includes(query)
}

/**
 * Whether a licence list item belongs to the chosen telephony system. For a telsys-scoped admin the
 * list endpoint can return other systems' rows too; those must not be written with this system's
 * licence profile.
 *
 * @param item Licence list item.
 * @param telSysId The chosen telephony system.
 * @returns True when the item has no system of its own or is on the chosen one.
 */
export const isItemOnTelSys = (item: LicenceManagementListItem, telSysId: string): boolean =>
    IsVarEmpty(item.TelSysID) || item.TelSysID === Number(telSysId)

/** Compares two endpoint values, treating missing values as empty and numbers as their text. */
const isSameEndpointValue = (a: Nullable<string | number>, b: Nullable<string | number>): boolean =>
    String(a ?? EMPTY_STRING) === String(b ?? EMPTY_STRING)

/** Compares an endpoint value only when the create sent one, since CCS may normalise an empty one. */
const isSameSentEndpointValue = (saved: Nullable<string | number>, sent: Nullable<string | number>): boolean =>
    IsVarEmpty(sent) || String(sent) === EMPTY_STRING || isSameEndpointValue(saved, sent)

/**
 * Whether a user read back after a failed create is the user that create sent: the same reporting
 * licence, telsys and partition, and the same device and agent where the create sent them. A user
 * that merely shares the username (an orphaned, unlisted or other-system user) does not count, so
 * the create is not reported as saved.
 *
 * @param saved User read back by username.
 * @param sent Body the create sent.
 * @returns True when the create evidently saved.
 */
export const isSavedAsSent = (saved: Definable<ApplicationUser>, sent: AdminUserBody): boolean =>
    isVarNotEmpty(saved) &&
    isStringNotEmpty(saved.Username) &&
    saved.Scope?.ReportingAccess === sent.Scope.ReportingAccess &&
    isSameEndpointValue(saved.Endpoint?.TelSysID, sent.Endpoint.TelSysID) &&
    isSameEndpointValue(saved.Endpoint?.PartIDInTelSys, sent.Endpoint.PartIDInTelSys) &&
    isSameSentEndpointValue(saved.Endpoint?.DeviceNumber, sent.Endpoint.DeviceNumber) &&
    isSameSentEndpointValue(saved.Endpoint?.AgentNumber, sent.Endpoint.AgentNumber)

/**
 * Whether a stored user holds admin rights, which a reporting licence cannot be given to (11299).
 *
 * @param user Stored user record.
 * @returns True for an admin user.
 */
export const isAdminUser = (user: Nullable<ApplicationUser>): boolean =>
    (user?.Permissions?.Admin?.length ?? 0) > 0 || user?.Scope?.Admin === true

/**
 * Builds the error raised when an admin user is refused, carrying the CCS code.
 *
 * @param language Loaded language handler.
 * @returns The error.
 */
export const adminUserError = (language: CLanguageHandler): RESTAPIError =>
    new RESTAPIError(
        0,
        null,
        language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Error_Admin),
        ADMIN_USER_ERROR_CODE
    )

/**
 * HTTP status of a failure, or 0 when it has none.
 *
 * @param error Failure.
 * @returns The status.
 */
export const errorStatus = (error: unknown): number => (error instanceof RESTAPIError ? error.HTTPStatus : 0)

/**
 * CCS error code of a failure, or an empty string when it has none.
 *
 * @param error Failure.
 * @returns The code.
 */
export const errorCode = (error: unknown): string =>
    error instanceof RESTAPIError && isStringNotEmpty(error.errorCode) ? String(error.errorCode) : EMPTY_STRING

/**
 * Whether a failure means the user was saved but a follow-up step (the welcome e-mail) failed.
 *
 * @param error Failure.
 * @returns True for a partial result.
 */
export const isPartialResult = (error: unknown): boolean =>
    error instanceof RESTAPIError && error.resultType === ResultType.Partial

/**
 * Whether a failure is a server error, after which CCS may still have saved the user.
 *
 * @param error Failure.
 * @returns True for a 5xx response.
 */
export const isServerError = (error: unknown): boolean => errorStatus(error) >= 500

/**
 * Whether a failure means the admin's session is gone, which stops the run: a 401, or a 403 that is
 * not a privilege error.
 *
 * @param error Failure.
 * @returns True when the run must stop.
 */
export const isSessionLoss = (error: unknown): boolean => {
    const status: number = errorStatus(error)
    const message: string = error instanceof Error ? error.message : EMPTY_STRING

    return status === 401 || (status === 403 && IsStringEmpty(errorCode(error)) && !/privilege/i.test(message))
}

/**
 * Describes a failure for the row: a friendly text, plus the HTTP status and CCS code.
 *
 * @param error Failure.
 * @param language Loaded language handler.
 * @returns The description.
 */
export const describeBulkError = (error: unknown, language: CLanguageHandler): BulkError => {
    const status: number = errorStatus(error)
    const code: string = errorCode(error)
    const raw: string = error instanceof Error ? error.message : String(error ?? EMPTY_STRING)
    const meta: string = [
        status > 0
            ? language.getInterpolated(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Meta_Http, status)
            : EMPTY_STRING,
        isStringNotEmpty(code)
            ? language.getInterpolated(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Meta_Code, code)
            : EMPTY_STRING
    ]
        .filter(isStringNotEmpty)
        .join(', ')
    let text: string = raw

    if (code === ADMIN_USER_ERROR_CODE) {
        text = language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Error_Admin)
    } else if (IsStringEmpty(raw)) {
        text = language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Error_Unknown)
    }

    return { text, meta, raw }
}

/**
 * Runs a task over items with a fixed number of workers, reporting progress as each one finishes.
 *
 * @param items Items to process.
 * @param concurrency Number of workers.
 * @param task Task run for each item; a rejection is counted, not thrown.
 * @param onProgress Called after each item with the number done.
 * @returns The number of items whose task failed.
 */
export const runWithConcurrency = async <T>(
    items: readonly T[],
    concurrency: number,
    task: (item: T) => Promise<void>,
    onProgress?: (done: number) => void
): Promise<number> => {
    let next: number = 0
    let done: number = 0
    let failed: number = 0

    const worker = async (): Promise<void> => {
        while (next < items.length) {
            const item: T = items[next]

            next += 1

            try {
                // Each worker handles its items in turn; the workers themselves run in parallel.
                // eslint-disable-next-line no-await-in-loop
                await task(item)
            } catch {
                failed += 1
            }

            done += 1

            if (isVarNotEmpty(onProgress)) onProgress(done)
        }
    }

    await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker))

    return failed
}
